import React, { useEffect, useMemo, useState } from "react";
import {
  CheckCircle2, Circle, Lock, Unlock, Star, ExternalLink, GraduationCap,
  Target, Users, Radio, Rocket, Save, ChevronDown, ChevronRight, UserCircle2,
  CalendarDays, BookOpen,
} from "lucide-react";
import { apiGet, apiPatch, apiPost, unpackList } from "../../Api/Client.js";
import { loadProgressView, saveOptimistically } from "./onboardingProgress.js";
import { resolveActiveEmployee } from "../../Shared/ScreenUtils.jsx";
import InfoTooltip from "../../Shared/InfoTooltip.jsx";
import { canRead } from "../../Services/pageAccess.js";
import { SlackIntegrationHealthBanner } from "../../Shared/ScreenComponents.jsx";

// Junior Onboarding — a trainee's ramp from day one to their first
// self-generated lead. Per-person, real, backed by RevOps.OnboardingProgress.
//
// Who can do what: anyone who holds junior_onboarding UPDATE (Bassi, today —
// "support admin and MD, manager of sales" is the intent, he's the only one
// right now) sees a person picker top-right and can edit. Everyone else sees
// their OWN row only, read-only — this is a reference page for them, not a
// self-editing one; the ladder is judged, not self-reported.

const card = {
  background: "var(--surface)", border: "1px solid var(--c-200)", borderRadius: 14,
  padding: "18px 20px",
};
const muted = { color: "var(--c-500)" };
const chip = (bg, fg) => ({
  display: "inline-flex", alignItems: "center", gap: 5, fontSize: 11, fontWeight: 700,
  letterSpacing: "0.04em", textTransform: "uppercase", padding: "3px 9px", borderRadius: 999,
  background: bg, color: fg,
});

const RESOURCES = [
  { label: "Sales Training doc (D1–D5 detail)", href: "https://docs.google.com/document/d/1it4O2ZZFY_SN0gSu1Z404fZAhs9OpZBkZhcvnERZ-CE/edit" },
  { label: "InterviewGod — ChatGPT project", href: "https://chatgpt.com/g/g-p-69c56bdc21e481919c0655baea294599-interviewgod/project" },
];

function ScoreRow({ dim, value, onChange, readOnly }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: "minmax(0,1fr) 190px 34px", alignItems: "center", gap: 14, padding: "9px 0", borderBottom: "1px solid var(--c-200)" }}>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: "var(--c-900)" }}>{dim.label}</div>
        <div style={{ fontSize: 11.5, ...muted }}>{dim.hint}</div>
      </div>
      {readOnly ? (
        <div style={{ height: 6, borderRadius: 999, background: "var(--c-100, rgba(120,120,120,.15))", overflow: "hidden" }}>
          <div style={{ height: "100%", width: `${value * 10}%`, background: "var(--blue-600)", borderRadius: 999 }} />
        </div>
      ) : (
        <input type="range" min={0} max={10} value={value} onChange={(e) => onChange(Number(e.target.value))} style={{ width: "100%", accentColor: "var(--blue-600)" }} />
      )}
      <div style={{ textAlign: "right", fontVariantNumeric: "tabular-nums", fontWeight: 700, color: value >= 7 ? "var(--green-600)" : value >= 4 ? "var(--blue-600)" : "var(--c-500)" }}>{value}</div>
    </div>
  );
}

function Task({ done, onToggle, readOnly, children }) {
  const Icon = done ? CheckCircle2 : Circle;
  return (
    <button
      onClick={readOnly ? undefined : onToggle}
      style={{ display: "flex", alignItems: "center", gap: 10, width: "100%", textAlign: "left", background: "transparent", border: "none", cursor: readOnly ? "default" : "pointer", padding: "8px 0", color: done ? "var(--c-500)" : "var(--c-900)" }}
    >
      <Icon size={18} color={done ? "var(--green-600)" : "var(--c-500)"} />
      <span style={{ fontSize: 14, textDecoration: done ? "line-through" : "none" }}>{children}</span>
    </button>
  );
}

function Section({ icon, badge, badgeStyle, title, subtitle, done, children, style }) {
  return (
    <div style={{ ...card, ...style }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 4 }}>
        {icon}
        <h2 style={{ margin: 0, fontSize: 17, color: done ? "var(--c-500)" : "var(--c-900)", textDecoration: done ? "line-through" : "none" }}>{title}</h2>
        {done && <CheckCircle2 size={16} color="var(--green-600)" />}
        {badge && <span style={badgeStyle}>{badge}</span>}
      </div>
      {subtitle && <p style={{ margin: "0 0 12px", fontSize: 13, ...muted }}>{subtitle}</p>}
      {children}
    </div>
  );
}

// A week folds away once you're past it and stays folded until you're on it.
// Only the week actually in play is open — finished weeks and weeks nobody has
// touched yet are noise on a page you read every day.
function WeekSection({ icon, title, subtitle, done, started, total, doneCount, open, onToggle, children }) {
  const state = done ? "done" : started ? "active" : "upcoming";
  const stateChip = {
    done: chip("var(--green-50, rgba(40,180,120,.14))", "var(--green-600)"),
    active: chip("var(--blue-50, rgba(80,120,255,.12))", "var(--blue-600)"),
    upcoming: chip("var(--c-100, rgba(120,120,120,.15))", "var(--c-500)"),
  }[state];
  return (
    <div style={{ ...card, padding: open ? "18px 20px" : "14px 20px", borderColor: state === "active" ? "var(--blue-600)" : "var(--c-200)" }}>
      <button
        onClick={onToggle}
        style={{ display: "flex", alignItems: "center", gap: 10, width: "100%", background: "transparent", border: "none", cursor: "pointer", textAlign: "left", padding: 0 }}
      >
        {open ? <ChevronDown size={16} color="var(--c-500)" style={{ flexShrink: 0 }} /> : <ChevronRight size={16} color="var(--c-500)" style={{ flexShrink: 0 }} />}
        {icon}
        <h2 style={{ margin: 0, fontSize: 16, flex: 1, minWidth: 0, color: done ? "var(--c-500)" : "var(--c-900)" }}>{title}</h2>
        <span style={{ fontSize: 12, ...muted, fontVariantNumeric: "tabular-nums" }}>{doneCount}/{total}</span>
        <span style={stateChip}>{state === "done" ? "done" : state === "active" ? "in progress" : "not started"}</span>
      </button>
      {open && (
        <div style={{ marginTop: 12 }}>
          {subtitle && <p style={{ margin: "0 0 12px", fontSize: 13, ...muted }}>{subtitle}</p>}
          {children}
        </div>
      )}
    </div>
  );
}

function PersonPicker({ people, selectedId, onSelect }) {
  return (
    <div style={{ position: "relative" }}>
      <select
        value={selectedId || ""}
        onChange={(e) => onSelect(Number(e.target.value))}
        style={{ appearance: "none", padding: "8px 32px 8px 14px", borderRadius: 999, border: "1px solid var(--c-200)", background: "var(--blue-50, rgba(80,120,255,.1))", color: "var(--blue-600)", fontSize: 13, fontWeight: 700, cursor: "pointer" }}
      >
        {people.map((p) => <option key={p.employee_id} value={p.employee_id}>{p.name}</option>)}
      </select>
      <ChevronDown size={14} style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", pointerEvents: "none" }} color="var(--blue-600)" />
    </div>
  );
}

const emptyLadder = () => ({ w0: {}, w1: [], w2: [], items: {}, unlocked: [] });

// Order on the page. Sales CRM access is injected right after w2 (see render)
// because it unlocks on a week-2 event.
const WEEK_SECTIONS = [
  { key: "w1", title: "Week 1 — Outreach & the demo", icon: Radio,
    subtitle: "No lead pressure. The one fixed output is a solution-based demo, every time." },
  { key: "w2", title: "Week 2 — Cold calling & discovery", icon: Users,
    subtitle: "The harder channel opens once the hesitation is already dead." },
  { key: "w34", title: "Weeks 3–4 — Coaching", icon: Target,
    subtitle: "Teardowns, the probation gates, and the first live meetings." },
  { key: "w58", title: "Weeks 5–8 — Running it alone", icon: Rocket,
    subtitle: "Quantifying pain live, then a full deal end to end with nobody on the line." },
];

// What kind of thing a row is. A trainee reads this page by scanning what type
// each row is before reading the words, so the type gets its own chip.
const KIND_STYLE = {
  activity: { bg: "var(--blue-50, rgba(80,120,255,.12))", fg: "var(--blue-600)", label: "Activity" },
  practice: { bg: "var(--amber-50, rgba(217,164,65,.16))", fg: "var(--gold-600, #a06d10)", label: "Practice" },
  skill: { bg: "var(--purple-50, rgba(140,110,200,.14))", fg: "var(--purple-600, #6b4fa0)", label: "Skill" },
  gate: { bg: "var(--green-50, rgba(40,180,120,.14))", fg: "var(--green-600)", label: "Gate" },
};

// Completion used to live in positional arrays (ladder.w1[2], ladder.w2[0]).
// Moving a task between weeks silently re-pointed somebody's tick at a
// different task, so it's keyed by stable item id now. This carries the old
// ticks across once, by the index they had when they were written — after the
// first save the `items` dict is the only thing read.
const LEGACY_INDEX = {
  linkedin: ["w1", 0],
  cold_calling: ["w1", 1],
  demo_practice: ["w1", 2],
  first_booked: ["w1", 3],
  discovery_to_demo: ["w2", 0],
};
function resolveItems(ladder) {
  const items = { ...(ladder.items || {}) };
  Object.entries(LEGACY_INDEX).forEach(([id, [arr, idx]]) => {
    if (items[id] === undefined && Array.isArray(ladder[arr]) && ladder[arr][idx]) items[id] = true;
  });
  return items;
}

function KindChip({ kind }) {
  const s = KIND_STYLE[kind] || KIND_STYLE.activity;
  return <span style={chip(s.bg, s.fg)}>{s.label}</span>;
}

// Checkbox and row are two separate hit targets on purpose: ticking something
// off and reading about it are different intentions.
function ItemRow({ item, done, onToggle, onOpen, readOnly, isOpen }) {
  // No content behind it means no arrow and no click target. An arrow that
  // opens an empty panel is worse than no arrow.
  const hasDetail = (item.blocks || []).length > 0;
  const label = (
    <>
      <span style={{ flex: 1, minWidth: 0, fontSize: 14, fontWeight: 600, color: done ? "var(--c-500)" : "var(--c-900)", textDecoration: done ? "line-through" : "none" }}>
        {item.title}
      </span>
      <KindChip kind={item.kind} />
      {hasDetail && <ChevronRight size={16} color={isOpen ? "var(--blue-600)" : "var(--c-400)"} style={{ flexShrink: 0 }} />}
    </>
  );
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 4, border: `1px solid ${isOpen ? "var(--blue-600)" : "var(--c-200)"}`, borderRadius: 12, background: "var(--surface)", marginBottom: 8 }}>
      <button
        onClick={readOnly ? undefined : onToggle}
        aria-label={done ? `Mark ${item.title} not done` : `Mark ${item.title} done`}
        style={{ flexShrink: 0, width: 44, height: 44, display: "flex", alignItems: "center", justifyContent: "center", background: "transparent", border: "none", cursor: readOnly ? "default" : "pointer", borderRadius: 10 }}
      >
        {done ? <CheckCircle2 size={19} color="var(--green-600)" /> : <Circle size={19} color="var(--c-400)" />}
      </button>
      {hasDetail ? (
        <button
          onClick={onOpen}
          style={{ flex: 1, minWidth: 0, display: "flex", alignItems: "center", gap: 10, background: "transparent", border: "none", cursor: "pointer", textAlign: "left", padding: "12px 12px 12px 2px", borderRadius: 10 }}
        >
          {label}
        </button>
      ) : (
        <div style={{ flex: 1, minWidth: 0, display: "flex", alignItems: "center", gap: 10, padding: "12px 12px 12px 2px" }}>
          {label}
        </div>
      )}
    </div>
  );
}

function DrawerBlock({ block }) {
  const label = block.label && (
    <div style={{ fontSize: 10.5, textTransform: "uppercase", letterSpacing: ".05em", color: "var(--c-500)", fontWeight: 700, marginBottom: 5 }}>{block.label}</div>
  );
  if (block.type === "list") {
    return (
      <div style={{ marginBottom: 16 }}>
        {label}
        <ul style={{ margin: 0, paddingLeft: 18 }}>
          {block.items.map((t, i) => <li key={i} style={{ fontSize: 13, lineHeight: 1.6, marginBottom: 6, color: "var(--c-800)" }}>{t}</li>)}
        </ul>
      </div>
    );
  }
  if (block.type === "quote") {
    return (
      <div style={{ marginBottom: 16 }}>
        {label}
        <div style={{ borderLeft: "3px solid var(--blue-600)", paddingLeft: 12, fontSize: 13, lineHeight: 1.6, fontStyle: "italic", color: "var(--c-600)" }}>{block.body}</div>
      </div>
    );
  }
  if (block.type === "table") {
    return (
      <div style={{ marginBottom: 16 }}>
        {label}
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12.5 }}>
            <thead>
              <tr>{block.head.map((h, i) => <th key={i} style={{ textAlign: "left", padding: "6px 8px", borderBottom: "1px solid var(--c-200)", color: "var(--c-500)", fontSize: 10.5, textTransform: "uppercase", letterSpacing: ".04em" }}>{h}</th>)}</tr>
            </thead>
            <tbody>
              {block.rows.map((r, i) => (
                <tr key={i}>{r.map((cellText, j) => <td key={j} style={{ padding: "6px 8px", borderBottom: "1px solid var(--c-100, rgba(120,120,120,.12))", color: "var(--c-800)", verticalAlign: "top" }}>{cellText}</td>)}</tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  }
  return (
    <div style={{ marginBottom: 16 }}>
      {label}
      <p style={{ margin: 0, fontSize: 13, lineHeight: 1.65, color: "var(--c-800)" }}>{block.body}</p>
    </div>
  );
}

// An in-page panel, not a modal. It sits inside the content column and the
// column shrinks to make room, so the header, the sidebar and the page behind
// it all stay fully usable and undimmed — opening the detail for one item
// should never take the whole screen hostage.
function ItemDrawer({ item, onClose }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);
  if (!item) return null;
  return (
    <aside
      style={{
        position: "sticky", top: 18, alignSelf: "flex-start",
        maxHeight: "calc(100vh - 36px)", overflowY: "auto",
        background: "var(--surface)", border: "1px solid var(--c-200)",
        borderRadius: 14, boxShadow: "0 6px 24px rgba(0,0,0,.08)",
      }}
    >
      <div style={{ position: "sticky", top: 0, background: "var(--surface)", borderBottom: "1px solid var(--c-200)", padding: "16px 18px 12px", display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, borderRadius: "14px 14px 0 0" }}>
        <div style={{ minWidth: 0 }}>
          <h3 style={{ margin: "0 0 6px", fontSize: 16, color: "var(--c-900)" }}>{item.title}</h3>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            <KindChip kind={item.kind} />
            {item.week && <span style={chip("var(--c-100, rgba(120,120,120,.15))", "var(--c-600)")}>{item.week}</span>}
          </div>
        </div>
        <button onClick={onClose} aria-label="Close" style={{ flexShrink: 0, width: 28, height: 28, borderRadius: 8, border: "1px solid var(--c-200)", background: "var(--surface)", cursor: "pointer", color: "var(--c-600)", fontSize: 14, lineHeight: 1 }}>✕</button>
      </div>
      <div style={{ padding: "16px 18px 28px" }}>
        {item.summary && <p style={{ margin: "0 0 16px", fontSize: 13, lineHeight: 1.6, color: "var(--c-600)" }}>{item.summary}</p>}
        {(item.blocks || []).map((b, i) => <DrawerBlock key={i} block={b} />)}
      </div>
    </aside>
  );
}

export default function JuniorOnboardingScreen({ data }) {
  const me = data?.me;
  // Deliberately NOT canUpdate(me, "junior_onboarding") — Ankit and Azaan hold that
  // update grant too (the mentor coupling), and a mentor seeing everyone else's
  // record — not just their own — is exactly the confusion Bassi flagged. The
  // picker and edit rights are superuser-only for now, full stop; a delegated
  // "manager" role is a real future need, but not this check.
  const canEdit = !!me?.user?.isSuperuser;
  const myEmployee = resolveActiveEmployee(data);

  const [ladderConfig, setLadderConfig] = useState(null); // rubric.junior.ladder
  const [stages, setStages] = useState([]);
  const [people, setPeople] = useState(null); // eligible people (canEdit only)
  const [selectedId, setSelectedId] = useState(null);
  const [progress, setProgress] = useState(null); // the OnboardingProgress row, or null
  const [progressLoading, setProgressLoading] = useState(false);
  const [progressFailed, setProgressFailed] = useState(false); // the load failed: not the same as "no record yet"
  const [error, setError] = useState("");
  const [notes, setNotes] = useState("");
  const [blockers, setBlockers] = useState("");
  const [notesDirty, setNotesDirty] = useState(false);
  const [notesBusy, setNotesBusy] = useState(false);
  const [openItemId, setOpenItemId] = useState(null);
  // null = follow the automatic rule (open the week in play). A click puts an
  // explicit true/false in here for that week only.
  const [weekOverride, setWeekOverride] = useState({});

  // Config + roster once.
  useEffect(() => {
    let cancelled = false;
    apiGet("/RevOps/rubric/").then((r) => {
      if (cancelled) return;
      setLadderConfig(r?.junior?.ladder || null);
      setStages(r?.junior?.stages || []);
    }).catch(() => {});
    if (canEdit) {
      apiGet("/RevOps/eligible-people/?track=junior")
        .then((r) => { if (!cancelled) { setPeople(r.people || []); setSelectedId((r.people || [])[0]?.employee_id || null); } })
        .catch((err) => !cancelled && setError(err?.message || "Failed to load the roster."));
    } else if (myEmployee?.id) {
      setSelectedId(myEmployee.id);
    }
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canEdit]);

  // Load the selected person's progress row whenever selection changes.
  useEffect(() => {
    if (!selectedId) return undefined;
    let cancelled = false;
    loadProgressView({
      fetchRow: async () => unpackList(await apiGet(`/RevOps/JuniorOnboardingProgress/?employee=${selectedId}`))[0] || null,
      isCurrent: () => !cancelled,
      show: (v) => { setProgress(v.progress); setNotes(v.notes); setBlockers(v.blockers); setNotesDirty(v.notesDirty); },
      onStart: () => { setProgressLoading(true); setProgressFailed(false); setError(""); },
      onFail: (err) => { setProgressFailed(true); setError(err?.message || "Failed to load onboarding progress."); },
      onDone: () => setProgressLoading(false),
    });
    return () => { cancelled = true; };
  }, [selectedId]);

  const ladder = progress?.ladder_state && Object.keys(progress.ladder_state).length ? progress.ladder_state : emptyLadder();

  const startOnboarding = async () => {
    try {
      const created = await apiPost("/RevOps/JuniorOnboardingProgress/", {
        employee: selectedId, stage: "week_0", ladder_state: emptyLadder(),
      });
      setProgress(created);
    } catch (err) {
      alert(err?.message || "Failed to start onboarding for this person.");
    }
  };

  const patchLadder = async (nextLadder) => {
    if (!progress) return;
    await saveOptimistically({
      progress,
      patch: { ladder_state: nextLadder },
      setProgress,
      save: () => apiPatch(`/RevOps/JuniorOnboardingProgress/${progress.id}/`, { ladder_state: nextLadder, inferred: false }),
      onFail: (err) => alert(err?.message || "Failed to save."),
    });
  };

  const setW0 = (key, value) => patchLadder({ ...ladder, w0: { ...ladder.w0, [key]: value } });
  const toggleItem = (id) => {
    const current = resolveItems(ladder);
    patchLadder({ ...ladder, items: { ...current, [id]: !current[id] } });
  };
  const toggleWeekLock = (key) => {
    const unlocked = new Set(ladder.unlocked || []);
    unlocked.has(key) ? unlocked.delete(key) : unlocked.add(key);
    patchLadder({ ...ladder, unlocked: Array.from(unlocked) });
  };

  const saveStage = async (stage) => {
    await saveOptimistically({
      progress,
      patch: { stage },
      setProgress,
      save: () => apiPatch(`/RevOps/JuniorOnboardingProgress/${progress.id}/`, { stage, inferred: false }),
      onFail: (err) => alert(err?.message || "Failed to save."),
    });
  };

  const saveNotes = async () => {
    setNotesBusy(true);
    try {
      const updated = await apiPatch(`/RevOps/JuniorOnboardingProgress/${progress.id}/`, { notes, blockers, inferred: false });
      setProgress(updated);
      setNotesDirty(false);
    } catch (err) {
      alert(err?.message || "Failed to save.");
    } finally {
      setNotesBusy(false);
    }
  };

  const w0Dims = ladderConfig?.w0_dimensions || [];
  const w0Days = ladderConfig?.w0_days || [];
  const weeks = ladderConfig?.weeks || [];
  const itemDefs = ladderConfig?.items || {};
  const weekItems = ladderConfig?.week_items || {};
  const doneItems = resolveItems(ladder);
  const itemsFor = (weekKey) => (weekItems[weekKey] || []).map((id) => ({ id, ...(itemDefs[id] || {}) })).filter((it) => it.title);
  const weekStats = (weekKey) => {
    const list = weekItems[weekKey] || [];
    const doneCount = list.filter((id) => doneItems[id]).length;
    return { total: list.length, doneCount, done: list.length > 0 && doneCount === list.length, started: doneCount > 0 };
  };
  const allDone = (weekKey) => weekStats(weekKey).done;
  const openItem = openItemId && itemDefs[openItemId]
    ? { id: openItemId, ...itemDefs[openItemId] }
    : null;

  const w0Avg = useMemo(() => {
    if (!w0Dims.length) return 0;
    const vals = w0Dims.map((d) => Number(ladder.w0?.[d.key] || 0));
    return Math.round((vals.reduce((a, b) => a + b, 0) / vals.length) * 10) / 10;
  }, [ladder.w0, w0Dims]);

  // CRM access unlocks on the first self-sourced booked meeting, not on the
  // whole week being ticked — that's the real signal it's meant to gate on.
  const generatedFirstLead = !!doneItems.first_booked;

  const selectedPerson = people?.find((p) => p.employee_id === selectedId);
  const displayName = canEdit ? (selectedPerson?.name || "…") : (myEmployee?.display_name || "Your");
  const hasCrmAccess = canEdit ? !!selectedPerson?.has_crm_access : canRead(me, "sales_crm");

  // Playbook opens coming out of the Week 3–4 coaching. Gated on the coaching
  // gate itself rather than on the whole block being ticked — that gate is the
  // real signal, the same way the first booked meeting is for CRM.
  const clearedCoaching = !!doneItems.probation_gates;
  const hasPlaybookAccess = canEdit ? !!selectedPerson?.has_playbook_access : canRead(me, "playbook");

  const grantPage = async (page, field) => {
    if (!selectedPerson) return;
    try {
      await apiPost(`/Users/admin-panel/users/${selectedPerson.user_id}/action/`, {
        action: "set_page_flag", page, page_action: "read", allow: true,
      });
      setPeople((cur) => cur.map((p) => (p.employee_id === selectedId ? { ...p, [field]: true } : p)));
    } catch (err) {
      alert(err?.message || "Failed to grant access.");
    }
  };
  const grantCrm = () => grantPage("sales_crm", "has_crm_access");
  const grantPlaybook = () => grantPage("playbook", "has_playbook_access");

  return (
    <section className="Screen-Stack" style={{ padding: "26px 30px", maxWidth: 980, margin: "0 auto" }}>
      <SlackIntegrationHealthBanner />
      <section className="Page-Heading" style={{ marginBottom: 6, display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
        <div>
          <span>Onboarding</span>
          <h1>{displayName}{canEdit ? "'s" : ""} Onboarding</h1>
        </div>
        {canEdit && people && people.length > 0 && (
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <PersonPicker people={people} selectedId={selectedId} onSelect={setSelectedId} />
            <InfoTooltip>
              <strong style={{ display: "block", marginBottom: 6, color: "var(--c-900)" }}>How this list is built</strong>
              Whoever holds <b>read</b> access to the Junior Onboarding page shows up here — nothing more.
              There's no separate roster; the access grant IS the definition of who's on this track.
              <br /><br />
              That's normally every intern in Sales — they get it by default from their
              department. Full-timers in Sales (a senior like Ankit or Azaan) end up with it
              too, because the same underlying grant that opens their own Sales Onboarding page happens to
              open this one as well — which in practice is useful, since a senior finishing the junior
              material is exactly the kind of person worth having visibility here.
              <br /><br />
              <strong style={{ color: "var(--c-900)" }}>To onboard someone new:</strong> Admin Panel → People →
              click their access → tick <b>Junior Onboarding: read</b>. The moment that's granted, their
              name appears in this list, with an empty record ready to fill in.
              <br /><br />
              Only people who hold <b>update</b> on this page see the dropdown at all — today that's you.
            </InfoTooltip>
          </div>
        )}
      </section>
      <p style={{ margin: "0 0 20px", ...muted, fontSize: 14 }}>
        {canEdit
          ? "The ramp, per person — week by week. Everything here auto-saves as you touch it."
          : "Your ramp, from day one to your first self-generated lead. This is a reference page — your progress is judged and updated by your manager, not self-reported."}
      </p>

      {error && <div className="Login-Error">{error}</div>}

      {canEdit && people && people.length === 0 && (
        <div style={{ ...card, borderStyle: "dashed" }}>
          <p style={{ margin: 0, fontSize: 13, ...muted }}>
            Nobody currently holds Junior Onboarding access, so there's nobody to show here yet —
            grant it to someone from the admin panel's People list first.
          </p>
        </div>
      )}

      {selectedId && progressLoading && <p style={muted}>Loading…</p>}

      {selectedId && !progressLoading && !progressFailed && !progress && (
        <div style={{ ...card, borderStyle: "dashed", textAlign: "center" }}>
          <UserCircle2 size={22} color="var(--c-400)" />
          <p style={{ margin: "8px 0 0", fontSize: 13, ...muted }}>
            {canEdit
              ? "No onboarding record for this person yet."
              : "No onboarding record for you yet. Please ask your mentor to start your onboarding."}
          </p>
          {canEdit && (
            <button className="Primary-Button" onClick={startOnboarding} style={{ marginTop: 10 }}>
              Start onboarding
            </button>
          )}
        </div>
      )}

      {selectedId && !progressLoading && progress && (
        // Two tracks: the curriculum, and the detail panel beside it. When the
        // panel opens the curriculum column narrows to make room rather than
        // being covered over — the page stays readable and the header untouched.
        <div style={{ display: "grid", gridTemplateColumns: openItem ? "minmax(0,1fr) minmax(300px, 400px)" : "minmax(0,1fr)", gap: 16, alignItems: "start" }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 16, minWidth: 0 }}>

          {/* Current stage */}
          <div style={{ ...card, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
            <div>
              <strong style={{ fontSize: 14, color: "var(--c-900)" }}>Current stage</strong>
              {progress.inferred && <div style={{ fontSize: 11, ...muted, marginTop: 2 }}>Inferred from the session record — not yet confirmed.</div>}
            </div>
            {canEdit ? (
              <select
                value={progress.stage}
                onChange={(e) => saveStage(e.target.value)}
                style={{ padding: "6px 12px", borderRadius: 999, border: "1px solid var(--c-200)", background: "var(--blue-50, rgba(80,120,255,.1))", color: "var(--blue-600)", fontSize: 12, fontWeight: 700 }}
              >
                {stages.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
              </select>
            ) : (
              <span style={chip("var(--blue-50, rgba(80,120,255,.1))", "var(--blue-600)")}>{progress.stage_label}</span>
            )}
          </div>

          {/* SLT — kept aside, for reference only */}
          <div style={{ ...card, background: "transparent", borderStyle: "dashed" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <GraduationCap size={18} color="var(--c-500)" />
              <strong style={{ color: "var(--c-600)", fontSize: 14 }}>SLT — Shortlisting Tasks</strong>
              <span style={chip("var(--c-100, rgba(120,120,120,.15))", "var(--c-500)")}>reference</span>
            </div>
            <div style={{ ...muted, fontSize: 12.5, marginTop: 6, lineHeight: 1.6 }}>
              D1–D3 Communication + Business Articulation · D3–D6 Feature Understanding & Solution-Based Demo (Product + FAQ).
              <br />Check: a solution-based demo, once the discovery videos are given.
            </div>
          </div>

          {/* Week 0 — positioning scorecard */}
          <Section
            icon={<Target size={19} color="var(--blue-600)" />}
            title="Week 0 — Positioning"
            badge={`Readiness ${w0Avg}/10`}
            badgeStyle={chip("var(--blue-50, rgba(80,120,255,.12))", "var(--blue-600)")}
            subtitle="A read of how strong they are on each dimension (0–10), not a checklist. Gate: proper business & personal positioning."
          >
            {w0Dims.map((d) => (
              <ScoreRow key={d.key} dim={d} value={Number(ladder.w0?.[d.key] || 0)} onChange={(v) => setW0(d.key, v)} readOnly={!canEdit} />
            ))}
          </Section>

          {/* Week 0 is mentor-delivered, day by day — nothing to tick off here. */}
          {w0Days.length > 0 && (
            <Section
              icon={<CalendarDays size={19} color="var(--blue-600)" />}
              title="Week 0 — the reset, day by day"
              subtitle="Delivered live by your mentor. There's nothing to tick here — this is what gets taught."
            >
              {w0Days.map((d, i) => (
                <div key={i} style={{ display: "grid", gridTemplateColumns: "72px 1fr", gap: 14, padding: "10px 0", borderTop: i === 0 ? "none" : "1px solid var(--c-200)" }}>
                  <div style={{ fontSize: 12.5, fontWeight: 700, color: "var(--blue-600)" }}>{d.day}</div>
                  <div>
                    <div style={{ fontSize: 13.5, fontWeight: 700, color: "var(--c-900)", marginBottom: 3 }}>{d.title}</div>
                    <div style={{ fontSize: 12.5, lineHeight: 1.6, ...muted }}>{d.body}</div>
                  </div>
                </div>
              ))}
            </Section>
          )}

          {WEEK_SECTIONS.map((wk) => {
            const st = weekStats(wk.key);
            const auto = st.started && !st.done;             // the week in play
            const isOpen = weekOverride[wk.key] ?? auto;
            const Icon = wk.icon;
            return (
              <React.Fragment key={wk.key}>
                <WeekSection
                  icon={<Icon size={18} color="var(--blue-600)" />}
                  title={wk.title}
                  subtitle={wk.subtitle}
                  done={st.done}
                  started={st.started}
                  total={st.total}
                  doneCount={st.doneCount}
                  open={isOpen}
                  onToggle={() => setWeekOverride((o) => ({ ...o, [wk.key]: !isOpen }))}
                >
                  {itemsFor(wk.key).map((it) => (
                    <ItemRow
                      key={it.id}
                      item={it}
                      done={!!doneItems[it.id]}
                      onToggle={() => toggleItem(it.id)}
                      onOpen={() => setOpenItemId(openItemId === it.id ? null : it.id)}
                      readOnly={!canEdit}
                      isOpen={openItemId === it.id}
                    />
                  ))}
                </WeekSection>

                {/* CRM access sits immediately after Week 2 — it unlocks on the
                    first self-sourced booked meeting, which is a week-2 event,
                    not something that belongs at the bottom of the page. */}
                {wk.key === "w2" && (
                  <Section
                    icon={<Rocket size={19} color={hasCrmAccess ? "var(--green-600)" : "var(--c-500)"} />}
                    title="Sales CRM access"
                    subtitle="Unlocks once they've generated their own leads. This reflects their real access grant, not a separate flag."
                    style={hasCrmAccess ? { borderColor: "var(--green-500)" } : {}}
                  >
                    {hasCrmAccess ? (
                      <div style={{ ...chip("var(--green-50, rgba(40,180,120,.14))", "var(--green-600)"), fontSize: 13 }}>
                        <CheckCircle2 size={15} /> Sales CRM access granted
                      </div>
                    ) : canEdit ? (
                      <button
                        className="Primary-Button"
                        disabled={!generatedFirstLead}
                        onClick={grantCrm}
                        style={!generatedFirstLead ? { opacity: 0.5, cursor: "not-allowed" } : {}}
                        title={generatedFirstLead ? "Grant this trainee real access to the live Sales CRM" : "Locked until they've booked their first self-sourced meeting"}
                      >
                        {generatedFirstLead ? <>Grant Sales CRM access <CheckCircle2 size={15} /></> : <><Lock size={14} /> Locked — no self-sourced meeting yet</>}
                      </button>
                    ) : (
                      <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, ...muted }}>
                        <Lock size={14} /> Locked
                      </div>
                    )}
                  </Section>
                )}

                {/* Playbook access sits immediately after Weeks 3–4, for the
                    same reason CRM sits after Week 2: it unlocks coming out of
                    the coaching, not at the bottom of the page. Read earlier it
                    is vocabulary to recite; read after four weeks of live calls
                    every line attaches to a conversation they have had. */}
                {wk.key === "w34" && (
                  <Section
                    icon={<BookOpen size={19} color={hasPlaybookAccess ? "var(--green-600)" : "var(--c-500)"} />}
                    title="Playbook access"
                    subtitle="Unlocks once the coaching checkpoints are cleared. This reflects their real access grant, not a separate flag."
                    style={hasPlaybookAccess ? { borderColor: "var(--green-500)" } : {}}
                  >
                    {hasPlaybookAccess ? (
                      <div style={{ ...chip("var(--green-50, rgba(40,180,120,.14))", "var(--green-600)"), fontSize: 13 }}>
                        <CheckCircle2 size={15} /> Playbook access granted
                      </div>
                    ) : canEdit ? (
                      <button
                        className="Primary-Button"
                        disabled={!clearedCoaching}
                        onClick={grantPlaybook}
                        style={!clearedCoaching ? { opacity: 0.5, cursor: "not-allowed" } : {}}
                        title={clearedCoaching ? "Open the deal playbook for this trainee" : "Locked until the two probation checkpoints are cleared"}
                      >
                        {clearedCoaching ? <>Grant playbook access <CheckCircle2 size={15} /></> : <><Lock size={14} /> Locked — coaching checkpoints not cleared</>}
                      </button>
                    ) : (
                      <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, ...muted }}>
                        <Lock size={14} /> Locked
                      </div>
                    )}
                  </Section>
                )}
              </React.Fragment>
            );
          })}


          {/* Notes — session-by-session record */}
          <div style={card}>
            <strong style={{ fontSize: 14, color: "var(--c-900)", display: "block", marginBottom: 8 }}>What we've seen — session by session</strong>
            <textarea
              rows={9}
              value={notes}
              readOnly={!canEdit}
              onChange={(e) => { setNotes(e.target.value); setNotesDirty(true); }}
              style={{ width: "100%", resize: "vertical", fontSize: 13, lineHeight: 1.6, padding: 8, borderRadius: 8, border: "1px solid var(--c-200)", marginBottom: 8, fontFamily: "inherit", background: canEdit ? undefined : "var(--surface-soft, #f5f5f5)" }}
            />
            <label style={{ display: "block", fontSize: 11.5, fontWeight: 700, color: "var(--c-600)", marginBottom: 4 }}>What's holding them at this rung</label>
            <textarea
              rows={3}
              value={blockers}
              readOnly={!canEdit}
              onChange={(e) => { setBlockers(e.target.value); setNotesDirty(true); }}
              style={{ width: "100%", resize: "vertical", fontSize: 13, lineHeight: 1.6, padding: 8, borderRadius: 8, border: "1px solid var(--c-200)", marginBottom: canEdit ? 10 : 0, fontFamily: "inherit", background: canEdit ? undefined : "var(--surface-soft, #f5f5f5)" }}
            />
            {canEdit && (
              <button className="Primary-Button" onClick={saveNotes} disabled={notesBusy || !notesDirty} style={{ opacity: notesDirty ? 1 : 0.5 }}>
                {notesBusy ? "Saving…" : "Save"} <Save size={14} />
              </button>
            )}
          </div>

          {/* References — behind onboarding, not a general-purpose links card.
              Only visible once a record exists, same as everything else here. */}
          <div style={card}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
              <Star size={15} color="var(--gold-500, #d9a441)" />
              <strong style={{ color: "var(--c-900)", fontSize: 14 }}>References</strong>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {RESOURCES.map((r) => (
                <a key={r.href} href={r.href} target="_blank" rel="noreferrer" style={{ display: "inline-flex", alignItems: "center", gap: 7, color: "var(--blue-600)", fontSize: 13.5, textDecoration: "none" }}>
                  <ExternalLink size={14} /> {r.label}
                </a>
              ))}
            </div>
          </div>
        </div>

          {openItem && <ItemDrawer item={openItem} onClose={() => setOpenItemId(null)} />}
        </div>
      )}
    </section>
  );
}
